import { resolveDesignProfile, type ElectricalDesignPolicy } from "./design-profiles";
import { getDefinition, type DefLookup } from "./catalog";
import { splitV, type Vertex } from "./diagnostics/graph";
import { isStudTerminal } from "./effective";
import { collectChargerOutputCircuits } from "./charger-output-circuits";
import { collectLoadCircuits } from "./load-circuits";
import { indexNodesById } from "./node-tree";
import { measure } from "./perf-marks";
import { VDROP_PCT_CRITICAL, VDROP_PCT_NON_CRITICAL } from "./wire-tables";
import type {
  Cable,
  ComponentDefinition,
  System,
  SystemNode,
  VdropClass,
} from "./types";

export { VDROP_PCT_CRITICAL, VDROP_PCT_NON_CRITICAL };

/** Which side of ABYC E-11.14.2.6 a circuit falls on; `null` when the catalog can't tell. */
export type { VdropClass };

export function vdropPctForClass(cls: VdropClass, policy = resolveDesignProfile({}).policy.voltageDrop): number {
  return cls === "critical" ? policy.criticalPct : policy.nonCriticalPct;
}

/** Catalog ids E-11.14.2.6 puts on the 3 % side, grouped by the clause phrase. */
const CRITICAL_LOAD_IDS = new Set<string>([
  // Reviewed electronic and manufacturer-limited supply circuits.
  "load-actisense-ngw1-iso",
  "load-airmar-dst810",
  "load-alarm-linnepe-trigas",
  "load-em-trak-s100",
  "load-fridge-dometic-cd30",
  "load-fridge-thetford-t2090c",
  "load-garmin-gsd25",
  "load-heater-propex-hs2000",
  "load-heater-webasto-thermo-top-evo5",
  "load-icom-m330ge",
  "load-icom-m510e",
  "load-raritan-marine-elegance-220hf012",
  "load-router-peplink-br1-mini-hw3",
  "load-rule-33ala",
  "load-tv-avtex-w215ts",
  "load-waterheater-truma-aquago-comfort",
  "load-yachtdevices-ydnr02",
  // "navigation lights"
  "load-nav-lanterns-led",
  "load-nav-lanterns-incand",
  "load-hella-naviled-trio",
  // "bilge blowers" — a bilge pump is the same safety-of-life circuit (Blue Sea, West Marine).
  "load-bilge-pump",
  "load-rule-mate-1100",
  "load-attwood-turbo-4000",
  // "electronic equipment"
  "load-chartplotter",
  "load-vhf-radio",
  "load-ais",
  "load-autopilot",
  "load-starlink-mini",
  "load-router-12v",
  "load-bms-monitor",
  "load-standard-horizon-gx1400gps",
  "load-em-trak-b923",
  "load-raymarine-ev100-wheel",
  "load-raymarine-quantum2",
  "load-raymarine-axiom-plus-9",
  "load-bg-triton2",
  "load-router-teltonika-rutx11",
]);

/**
 * The 10 % side: E-11.14.2.6's "lighting, other than navigation lights".
 * Accessory outlets are left out — a socket's load is unknown at design time.
 */
const NON_CRITICAL_LOAD_IDS = new Set<string>([
  "load-light-hella-euroled75-dual",
  "load-light-fiamma-awning-98655-848",
  "load-oceanled-x4",
  "load-led-down",
  "load-reading-lamp",
  "load-led-strip",
]);

export function classifyLoadDefinition(
  def: ComponentDefinition | undefined,
  policy?: ElectricalDesignPolicy,
): VdropClass | null {
  if (!def || policy?.voltageDrop.classifyCatalogLoads === false) return null;
  if (CRITICAL_LOAD_IDS.has(def.id)) return "critical";
  if (NON_CRITICAL_LOAD_IDS.has(def.id)) return "non-critical";
  // Monitors, shunts and GX displays are E-11's "electronic equipment".
  if (def.category === "monitor") return "critical";
  return null;
}

/**
 * True when this cable is a "panelboard or switchboard main feeder" — the first clause
 * of E-11.14.2.6's 3 % list. A busbar not in fusebox mode is excluded on purpose:
 * calling every bus feed critical would silently upsize the whole system.
 */
function isMainFeeder(
  cable: Cable,
  nodesById: Record<string, SystemNode>,
  defLookup: DefLookup,
): boolean {
  const ends: Array<[string, string]> = [
    [cable.fromNodeId, cable.fromTerminalId],
    [cable.toNodeId, cable.toTerminalId],
  ];
  for (const [nodeId, terminalId] of ends) {
    const node = nodesById[nodeId];
    if (node?.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def) continue;

    if (def.renderVariant === "busbar") {
      if (!node.isFusebox) continue;
      // A grown stud's `stud-N` id isn't in `def.terminals`, so it falls out here.
      const t = def.terminals.find((x) => x.id === terminalId);
      if (t && t.polarity === "positive" && !isStudTerminal(def, t)) return true;
      continue;
    }

    if (!def.isFuseBlock) continue;
    if (def.renderVariant === "fuse-holder") continue;
    const t = def.terminals.find((x) => x.id === terminalId);
    if (t && t.direction === "input" && t.polarity === "positive") return true;
  }
  return false;
}

export type VdropAllowances = {
  /** Percent, by cable id. Feeds `computeCableSpec`'s V-drop budget. */
  byCable: Map<string, number>;
  /** Percent, by the load's positive-terminal vertex. */
  byLoadVertex: Map<Vertex, number>;
  defaultPct: number;
};

const allowanceCache = new WeakMap<System, WeakMap<DefLookup, Map<number, VdropAllowances>>>();

/**
 * Every cable's and every load's voltage-drop allowance per ABYC E-11.14.2.6.
 * Not simulate-aware on purpose — an open switch would strand a lighting branch
 * and size the same cable differently in the two modes.
 */
export function computeVdropAllowances(
  system: System,
  defLookup: DefLookup = getDefinition,
  defaultPct = VDROP_PCT_CRITICAL,
): VdropAllowances {
  let byDefinition = allowanceCache.get(system);
  if (!byDefinition) {
    byDefinition = new WeakMap();
    allowanceCache.set(system, byDefinition);
  }
  let byPct = byDefinition.get(defLookup);
  if (!byPct) {
    byPct = new Map();
    byDefinition.set(defLookup, byPct);
  }
  const cached = byPct.get(defaultPct);
  if (cached) return cached;
  const result = measure("computeVdropAllowances", () =>
    computeVdropAllowancesImpl(system, defLookup, defaultPct),
  );
  byPct.set(defaultPct, result);
  return result;
}

function computeVdropAllowancesImpl(
  system: System,
  defLookup: DefLookup,
  defaultPct: number,
): VdropAllowances {
  const policy = resolveDesignProfile(system).policy;
  const byCable = new Map<string, number>();
  const byLoadVertex = new Map<Vertex, number>();
  const nodesById = indexNodesById(system.nodes);
  const cablesById = new Map(system.cables.map((c) => [c.id, c]));

  const tighten = (map: Map<string, number>, key: string, pct: number): void => {
    const cur = map.get(key);
    if (cur == null || pct < cur) map.set(key, pct);
  };

  // Both legs: E-11.14.2.6 measures the drop out and back, so a lighting
  // circuit's return is on the 10 % table with its feed, not on the default.
  for (const circuit of collectLoadCircuits(system, defLookup)) {
    const cableIds = [...circuit.pos, ...circuit.neg];

    let overridePct: number | null = null;
    for (const id of cableIds) {
      const cls = cablesById.get(id)?.vdropClass;
      if (!cls) continue;
      const pct = vdropPctForClass(cls, policy.voltageDrop);
      if (overridePct == null || pct < overridePct) overridePct = pct;
    }
    let inferredPct = defaultPct;
    const { nodeId } = splitV(circuit.loadVertex);
    const node = nodesById[nodeId];
    if (node?.kind === "component") {
      const cls = classifyLoadDefinition(
        defLookup(node.definitionId, node.customDefinition),
        policy,
      );
      if (cls) inferredPct = vdropPctForClass(cls, policy.voltageDrop);
    }
    const pathPct = overridePct ?? inferredPct;
    tighten(byLoadVertex as Map<string, number>, circuit.loadVertex, pathPct);
    for (const id of cableIds) tighten(byCable, id, pathPct);
  }

  for (const c of system.cables) {
    if (c.vdropClass) {
      byCable.set(c.id, vdropPctForClass(c.vdropClass, policy.voltageDrop));
    } else if (policy.voltageDrop.mainFeederPct != null && isMainFeeder(c, nodesById, defLookup)) {
      tighten(byCable, c.id, policy.voltageDrop.mainFeederPct);
    } else if (!byCable.has(c.id)) {
      byCable.set(c.id, defaultPct);
    }
  }

  // A recognised manufacturer maximum cannot be loosened by a generic
  // target or a per-cable class override. The full-loop budget is separate.
  for (const circuit of collectChargerOutputCircuits(system, defLookup)) {
    if (circuit.manufacturerAllowancePct == null) continue;
    for (const id of [...circuit.pos, ...circuit.neg]) tighten(byCable, id, circuit.manufacturerAllowancePct);
  }

  return { byCable, byLoadVertex, defaultPct };
}
